/**
 * The crew's "Arrived" step (1.4.0) and the tenant's permission to enter (1.4.0).
 *
 * Arrived comes before Finish on today's visits. Three answers, each through a path that already exists:
 *   home        — the visit starts (Queue 1 Start Service); optional Before photos.
 *   noAnswer    — Queue 1 Record No Show, "no answer at the door"; optional note and photo.
 *   doorCancel  — a reason, then "Come back later today?": a free spot today (a same-day Reschedule with
 *                 RequestedCapacityPosition — only when HK_PICK_SPOT_ENABLED is yes), Needs a new day (the
 *                 late cancel + an SM_FollowUpActions row), or just the late cancel.
 * The crew sees today only and never books anyone new; every press is audited with who and why.
 *
 * 1.5.16: the crew's card has two buttons (Arrived, then Finish) and Arrived has four answers:
 *   home        — as above.
 *   noAnswer    — "Tenant is not home — try again later today": nothing goes to the scheduler and the visit stays
 *                 Scheduled; the office gets an item under Needs your attention (an SM_FollowUpActions row, NOT_HOME)
 *                 and decides. The crew may press Arrived again when they try again.
 *   doorCancel  — as above.
 *   permission  — "Tenant is not home — permission to enter is on file": starts the visit exactly like home,
 *                 recorded as entered with permission. Refused when no permission is on file for this visit.
 *
 * Entry permission: "Tenant won't be home" queues an email with the tenant's own one-time link; their Yes
 * (a typed name, like a signature) is a dated permission for that one visit — never standing.
 */
import type { ReasonCategory } from './envelopes/queue1';

export type ArrivalAnswer = 'home' | 'noAnswer' | 'doorCancel' | 'permission';
/** The answers that start the visit (the crew is inside): the next press is Finish. */
export const startsVisit = (answer: ArrivalAnswer | string | null | undefined): boolean => answer === 'home' || answer === 'permission';

/**
 * "Not home — try again later today" (1.5.16): the SM_FollowUpActions row that puts the visit under the office's
 * Needs your attention. Same shape as a "Needs a new day" row (shared/followups.ts) — every value a stored choice
 * value or a text the app writes; only the two Text columns that name the event differ.
 */
export const NOT_HOME = {
  /** SM_FollowUpActions.OriginatingEvent (Text) */
  OriginatingEvent: 'NOT_HOME_AT_DOOR',
  /** SM_FollowUpActions.ActionInstanceTag (Text) */
  ActionInstanceTag: 'NOT-HOME',
} as const;
export const DOOR_REASONS = ['notReady', 'sick', 'later', 'other'] as const;
export type DoorReason = (typeof DOOR_REASONS)[number];
export type DoorNext = 'later' | 'newDay' | 'cancel';

/** The late cancel's reason category on Queue 1 (choice values exactly as stored). */
export const DOOR_REASON_CATEGORY: Record<DoorReason, ReasonCategory> = {
  notReady: 'Tenant Unavailable',
  sick: 'Health or Safety Concern',
  later: 'Tenant Unavailable',
  other: 'Other',
};

/** The words written into the request's note and the visit's history (English: the office's record). */
export const DOOR_REASON_WORDS: Record<DoorReason, string> = {
  notReady: 'not ready',
  sick: 'sick',
  later: 'asked to come back later',
  other: 'other',
};

export interface ArrivalRecord {
  appointmentId: string;
  tenantId: string;
  unit: string;
  dayKey: string;
  answer: ArrivalAnswer;
  reason: DoorReason | null;
  /** The crew's own words for "other" and the optional note — kept exactly as written. */
  words: string;
  next: DoorNext | null;
  /** The position the visit moved to (come back later today). */
  movedTo: number | null;
  /** The Queue 1 transaction(s) the press wrote. */
  txIds: string[];
  followUpId: string | null;
  photos: string[];
  /** Entry permission on file when the crew arrived (1.5.16: for `permission`, the crew entered with it). */
  withPermission: boolean;
  by: string;
  role: string;
  at: string;
  /** 1.6.5: the presser's crew (Admin → Users; A when none is set). */
  crew?: 'A' | 'B';
  /**
   * 1.7.8: the press was made later than the time it says — "they started at 2:30 and I'm recording it at 2:50".
   * `at` is the time entered; this is when it was entered ("recorded 2:50 by Elijah").
   */
  recordedAt?: string;
  /** 1.6.5: the press was taken back (Undo inside the window, or the office's "Clear the arrival stamp"). */
  undone?: import('./press').StampUndo | null;
}

/** What Today and History show for a visit's arrival. */
export interface ArrivalMarker {
  answer: ArrivalAnswer;
  reason: DoorReason | null;
  next: DoorNext | null;
  movedTo: number | null;
  dayKey: string;
  at: string;
  by: string;
  /** 1.6.5: the presser's crew. */
  crew?: 'A' | 'B';
  /** 1.7.8: when the time was entered, when that is later than the time itself. */
  recordedAt?: string;
}

export function arrivalMarker(r: ArrivalRecord): ArrivalMarker {
  return { answer: r.answer, reason: r.reason, next: r.next, movedTo: r.movedTo, dayKey: r.dayKey, at: r.at, by: r.by, ...(r.crew ? { crew: r.crew } : {}), ...(r.recordedAt ? { recordedAt: r.recordedAt } : {}) };
}

/* ---------------- entry permission ---------------- */

export type EntryPermissionStatus = 'asked' | 'granted' | 'declined';
/** How a permission on file was given (1.5.16): on the tenant's own emailed link, or to the office by phone. */
export type EntryHow = 'email' | 'phone';

export interface EntryPermissionRecord {
  appointmentId: string;
  tenantId: string;
  unit: string;
  dayKey: string;
  status: EntryPermissionStatus;
  askedBy: string;
  askedAt: string;
  communicationId: string | null;
  /** The tenant's answer on their own link: the name as typed, when, the link (its hash), the device. */
  answer: null | {
    agreed: boolean;
    typedName: string;
    at: string;
    linkHash: string;
    device: string;
    wordingVersion: string;
    wordingHash: string;
    pdfPath: string | null;
    copyCommunicationId: string | null;
  };
  /** 1.5.16: how the permission on file was given (absent on a 1.4.0 record: the tenant's link). */
  how?: EntryHow;
  /** 1.5.16: "Permission received by phone" — who gave it (as the office typed it), who recorded it, when. */
  phone?: {
    from: string;
    by: string;
    at: string;
    /** 1.5.17: read to the tenant by phone (Office action) — who read it (their name as typed), in which language, which terms, the PDF and its copy. */
    staffName?: string;
    lang?: 'en' | 'es';
    termsVersion?: string;
    termsHash?: string;
    pdfPath?: string;
    copyCommunicationId?: string | null;
  };
}

/** What Today and the crew's Arrived screen show. */
export interface EntryPermissionMarker {
  status: EntryPermissionStatus;
  askedAt: string;
  answeredAt: string | null;
  /** 1.5.16: for a permission on file — how it was given and when (null while there is none). */
  how?: EntryHow | null;
  at?: string | null;
}

export function entryMarker(r: EntryPermissionRecord): EntryPermissionMarker {
  const granted = r.status === 'granted';
  const how: EntryHow | null = granted ? (r.how ?? (r.phone ? 'phone' : 'email')) : null;
  return { status: r.status, askedAt: r.askedAt, answeredAt: r.answer?.at ?? null, how, at: granted ? (how === 'phone' ? (r.phone?.at ?? null) : (r.answer?.at ?? null)) : null };
}

/** A marker as Today carries it: one written before 1.5.16 has no `how` — a Yes then came from the tenant's link. */
export function entryMarkerFilled(m: EntryPermissionMarker): EntryPermissionMarker {
  const granted = m.status === 'granted';
  return { ...m, how: granted ? (m.how ?? 'email') : null, at: granted ? (m.at ?? m.answeredAt) : null };
}

/** The wording the tenant agrees to (plain language; the version is recorded with the answer). */
export const ENTRY_WORDING_VERSION = 'entry-permission-2026-10-01';
export const ENTRY_WORDING = {
  en: {
    title: 'Permission to enter your apartment',
    body: 'We understand you will not be home for your housekeeping visit on {date}. We need your clear permission for our team to enter your apartment when you are not there. This permission is only for this one visit.',
    yes: 'Yes, I agree',
    no: 'No',
    typeName: 'Type your full name to agree',
  },
  es: {
    title: 'Permiso para entrar a su apartamento',
    body: 'Entendemos que usted no estará en casa para su visita de limpieza del {date}. Necesitamos su permiso claro para que nuestro equipo entre a su apartamento cuando usted no esté. Este permiso es solo para esta visita.',
    yes: 'Sí, estoy de acuerdo',
    no: 'No',
    typeName: 'Escriba su nombre completo para aceptar',
  },
} as const;

/**
 * The two buttons on the HK_ENTRY_PERMISSION email (the Director's wording, 1 Oct 2026). Both open the
 * tenant's own page, where the answer is given (the Yes there is the signature); a GET never acts.
 */
export const ENTRY_BUTTONS = {
  en: { yes: 'Yes, I agree — the team may enter', no: "No, please don't enter" },
  es: { yes: 'Sí, autorizo — el equipo puede entrar', no: 'No, por favor no entren' },
} as const;

/** The entry-permission link page (kind `entry-permission`). */
export interface EntryLinkPage {
  state: 'ok' | 'answered' | 'closed';
  lang: 'en' | 'es';
  firstName: string;
  dayKey: string;
  unit: string;
  answered: null | { agreed: boolean; at: string };
  officePhone: string | null;
  wordingVersion: string;
}
